import { useEffect, useState } from 'react';
import { Key, Plus, Copy, Trash2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

export default function ApiKeysPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteKey, setDeleteKey] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});

  useEffect(() => {
    fetchApiKeys();
  }, [orgSlug]);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orgs/${orgSlug}/api-keys`);
      setApiKeys(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post(`/orgs/${orgSlug}/api-keys`, {
        name: newKeyName
      });
      
      // Show the created key
      setNewKeyValue(response.data.key || response.data.api_key || 'Key created');
      toast.success('API key created successfully');
      fetchApiKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
      setCreateOpen(false);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      await api.delete(`/orgs/${orgSlug}/api-keys/${keyId}`);
      toast.success('API key revoked successfully');
      setDeleteKey(null);
      fetchApiKeys();
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard');
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const maskKey = (key) => {
    if (!key) return '****';
    return key.substring(0, 8) + '...' + key.substring(key.length - 4);
  };

  const resetCreateDialog = () => {
    setCreateOpen(false);
    setNewKeyName('');
    setNewKeyValue('');
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-api-keys-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">
            API Keys
          </h1>
          <p className="text-[#8a93a3]">
            Manage external integration keys
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] w-fit"
          data-testid="api-keys-create-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </Button>
      </div>

      {/* API Keys Table */}
      <Card className="bg-[#161a26] border-[#252a37]">
        {loading ? (
          <div className="p-4 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-32 bg-[#1d2230]" />
                <Skeleton className="h-4 w-48 bg-[#1d2230]" />
                <Skeleton className="h-4 w-24 bg-[#1d2230]" />
              </div>
            ))}
          </div>
        ) : apiKeys.length > 0 ? (
          <Table data-testid="api-keys-table">
            <TableHeader>
              <TableRow className="border-b border-[#252a37] bg-[#1d2230]/50">
                <TableHead className="text-xs uppercase text-[#8a93a3]">Name</TableHead>
                <TableHead className="text-xs uppercase text-[#8a93a3]">Key</TableHead>
                <TableHead className="text-xs uppercase text-[#8a93a3]">Created</TableHead>
                <TableHead className="text-xs uppercase text-[#8a93a3]">Status</TableHead>
                <TableHead className="text-xs uppercase text-[#8a93a3] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys.map((apiKey) => (
                <TableRow 
                  key={apiKey.id} 
                  className="border-b border-[#252a37] hover:bg-[#1d2230]/30 transition-colors"
                >
                  <TableCell className="font-medium text-[#f0f2f7]">
                    {apiKey.name || 'Unnamed Key'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-sm text-[#8a93a3] bg-[#1d2230] px-2 py-1 rounded">
                        {visibleKeys[apiKey.id] ? (apiKey.key || apiKey.api_key || '****') : maskKey(apiKey.key || apiKey.api_key)}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-[#8a93a3] hover:text-[#f0f2f7]"
                        onClick={() => toggleKeyVisibility(apiKey.id)}
                      >
                        {visibleKeys[apiKey.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-[#8a93a3] hover:text-[#f0f2f7]"
                        onClick={() => handleCopyKey(apiKey.key || apiKey.api_key)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-[#8a93a3] text-sm">
                    {apiKey.created_at ? new Date(apiKey.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-[rgba(34,197,94,0.15)] text-[#22c55e] border border-[rgba(34,197,94,0.35)]">
                      Active
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteKey(apiKey)}
                      className="text-[#ef4444] hover:text-[#f05252] hover:bg-[rgba(239,68,68,0.1)]"
                      data-testid="api-keys-revoke-button"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <CardContent className="p-12 text-center">
            <Key className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">
              No API keys yet
            </h3>
            <p className="text-[#8a93a3] mb-4">
              Create an API key to enable external integrations
            </p>
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create API Key
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={resetCreateDialog}>
        <DialogContent className="bg-[#161a26] border-[#252a37]" data-testid="api-keys-create-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">Create API Key</DialogTitle>
            <DialogDescription className="text-[#8a93a3]">
              {newKeyValue 
                ? 'Copy your new API key. You won\'t be able to see it again.'
                : 'Enter a name for your new API key.'}
            </DialogDescription>
          </DialogHeader>
          
          {newKeyValue ? (
            <div className="space-y-4">
              <div className="p-4 bg-[#1d2230] rounded-lg border border-[#252a37]">
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono text-sm text-[#f5c518] break-all">
                    {newKeyValue}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCopyKey(newKeyValue)}
                    className="text-[#8a93a3] hover:text-[#f0f2f7] flex-shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#f5c518]">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>Make sure to copy your API key now. You won't be able to see it again!</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-[#8a93a3]">Key Name</Label>
                <Input
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Mobile App Integration"
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {newKeyValue ? (
              <Button
                onClick={resetCreateDialog}
                className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              >
                Done
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={resetCreateDialog}
                  className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateKey}
                  disabled={creating}
                  className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
                  data-testid="api-keys-create-dialog-submit-button"
                >
                  {creating ? 'Creating...' : 'Create Key'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteKey} onOpenChange={() => setDeleteKey(null)}>
        <AlertDialogContent className="bg-[#161a26] border-[#252a37]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f0f2f7]">Revoke API Key</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8a93a3]">
              Are you sure you want to revoke "{deleteKey?.name}"? Any integrations using this key will stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteKey(deleteKey?.id)}
              className="bg-[#ef4444] text-white hover:bg-[#f05252]"
            >
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
